import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { MapPin, Settings, Plus } from 'lucide-react';
import { LAP_COLORS, getLapColor, TB_LAP_INDEX, TB_COLOR } from '../LapSelector/LapSelector';
import { buildLookup, normalizeLookup, interpolateRelTime, pickBaseLap, lapDisplayNumber } from '../../utils/lapDelta';
import { lastCursor } from '../../utils/chartCursor';
import { speedColor } from '../../utils/speedColor';
import { arrayMin, arrayMax } from '../../utils/extent';
import HelpButton from '../HelpButton/HelpButton';
import styles from './TrackMap.module.css';

const M_TO_FT  = 3.28084;
const GMAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Must be called before the first importLibrary() — safe to call once at module level
setOptions({ key: GMAPS_KEY, v: 'weekly' });

// ── Color helpers (identical logic to the old Leaflet version) ────────────────
// speedColor moved to utils/speedColor.js — the public track pages bake the same
// ramp into their static SVG map from a Node build script.

function brakeColor(lonG) {
  // lon_g: positive = acceleration, negative = braking
  // green (accel) → white (coast) → red (brake)
  if (lonG == null) return '#888';
  const clamped = Math.max(-2, Math.min(2, lonG));
  if (clamped >= 0) {
    // 0 → 2G accel: white → green
    const t = clamped / 2;
    return `rgb(${Math.round((1 - t) * 255)},255,${Math.round((1 - t) * 255)})`;
  } else {
    // 0 → -2G brake: white → red
    const t = -clamped / 2;
    return `rgb(255,${Math.round((1 - t) * 255)},${Math.round((1 - t) * 255)})`;
  }
}

// Time gained/lost per metre vs the reference lap, mapped white→green (gaining)
// or white→red (losing). Full saturation at ±DELTA_FULL_SLOPE s/m — about half a
// second over a 100 m corner.
const DELTA_FULL_SLOPE = 0.005;
function deltaSlopeColor(slopePerM) {
  if (slopePerM == null) return '#888';
  const t = Math.max(-1, Math.min(1, slopePerM / DELTA_FULL_SLOPE));
  if (t >= 0) {
    return `rgb(255,${Math.round((1 - t) * 255)},${Math.round((1 - t) * 255)})`;
  }
  const k = -t;
  return `rgb(${Math.round((1 - k) * 255)},255,${Math.round((1 - k) * 255)})`;
}

function getPointColor(pt, colorMode, minSpeed, maxSpeed, lapColor) {
  if (colorMode === 'speed')    return speedColor(pt.speed, minSpeed, maxSpeed);
  if (colorMode === 'throttle') {
    // Keep isolated corrupt logger samples from producing invalid RGB values.
    const raw = Number(pt.throttle);
    const t = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw / 100)) : 0;
    return `rgb(${Math.round((1 - t) * 200)},${Math.round(t * 220)},20)`;
  }
  if (colorMode === 'brake')    return brakeColor(pt.lon_g);
  return lapColor;
}

// Color a point by which user-defined sector its distance falls in.
function sectorColor(distM, lapTotalDistM, sectors) {
  if (distM == null || !lapTotalDistM || !sectors?.length) return '#888';
  const frac = distM / lapTotalDistM;
  const idx = sectors.findIndex((s, i) =>
    frac >= s.start_frac && (frac < s.end_frac || i === sectors.length - 1));
  if (idx < 0) return '#888';
  return LAP_COLORS[idx % LAP_COLORS.length];
}

function findNearest(lat, lon, points) {
  let best = null, bestD = Infinity;
  for (const p of points) {
    const d = (p.lat - lat) ** 2 + (p.lon - lon) ** 2;
    if (d < bestD) { bestD = d; best = p; }
  }
  return best;
}

// Build SVG data-URI for corner badge icon
// Small checkered flag for the track's canonical start/finish point.
function sfIconUrl() {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="26">
    <line x1="3" y1="1" x2="3" y2="25" stroke="white" stroke-width="2"/>
    <rect x="4" y="1" width="16" height="12" fill="white" stroke="#333" stroke-width="0.5"/>
    <rect x="4" y="1" width="4" height="4" fill="#111"/><rect x="12" y="1" width="4" height="4" fill="#111"/>
    <rect x="8" y="5" width="4" height="4" fill="#111"/><rect x="16" y="5" width="4" height="4" fill="#111"/>
    <rect x="4" y="9" width="4" height="4" fill="#111"/><rect x="12" y="9" width="4" height="4" fill="#111"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function cornerIconUrl(label) {
  // Compact "Turn 10" / "T10" to just the number — the 28px badge fits ~4 chars,
  // so full "Turn N" labels would all truncate to an identical "Turn".
  const raw = (label || '').trim();
  const m    = raw.match(/^t(?:urn)?\s*(\d+[a-z]?)$/i);
  const text = (m ? `T${m[1]}` : raw).slice(0, 4);
  const fs     = text.length > 2 ? 8 : 10;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28">
    <circle cx="14" cy="14" r="12" fill="#e8411a" stroke="white" stroke-width="2"/>
    <text x="14" y="18" text-anchor="middle" fill="white"
          font-family="system-ui,sans-serif" font-size="${fs}" font-weight="bold">${text}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function TrackMap({
  laps,
  deltaBaseOverride = null,
  lapTraces, selectedLaps, corners,
  compareDrivers = [],
  customColors1 = null,
  startFinish = null,       // track's canonical S/F {lat, lon} — drawn as a flag marker
  onCornerMove, onCornerLabelChange, onCornerAdd, onCornerRemove,
  onRenumberTurns,
  canEditCorners = false,   // corner curation is admin-only (shared per-track defs)
  turnCount, onTurnCountChange,
  sensitivity = 'medium', onSensitivityChange,
  showTurnMarkers, onToggleShowTurnMarkers,
  showPerLapApex, onTogglePerLapApex,
  metrics,
  sectors = null,
  initialColorMode = 'lap',
}) {
  const isMobile = useIsMobile();
  const mapRef           = useRef(null);
  const mapInstanceRef   = useRef(null);
  const polylinesRef     = useRef([]);          // all GPS-trace Polyline objects
  const cornerMarkersRef = useRef({});          // corner_id → { marker, infoWindow }
  const apexMarkersRef   = useRef([]);          // per-lap apex circles
  const cornerHaloRef    = useRef(null);        // metrics-hover corner halo
  const crosshairRef     = useRef(null);        // hover circle — driver 1
  const crosshairRef2    = useRef(null);        // hover circle — driver 2
  const sfMarkerRef      = useRef(null);        // canonical start/finish flag
  const searchPointsRef  = useRef([]);
  const hoverPendingRef  = useRef(false);

  const [colorMode,        setColorMode]        = useState(initialColorMode);
  const [addMode,          setAddMode]          = useState(false);
  const [detectOpen,       setDetectOpen]       = useState(false);   // detection-settings popover
  const [showTb,           setShowTb]           = useState(false);   // TB hidden by default on the map
  const [didFit,           setDidFit]           = useState(false);
  const [mapsLoaded,       setMapsLoaded]       = useState(false);
  const [loadError,        setLoadError]        = useState(null);
  const [controlsVisible,  setControlsVisible]  = useState(false);
  const hideTimerRef = useRef(null);

  // Stable refs for callbacks used inside Google Maps event listeners
  const addModeRef     = useRef(false);
  const onCornerAddRef = useRef(onCornerAdd);

  useEffect(() => { addModeRef.current     = addMode; },        [addMode]);
  useEffect(() => { onCornerAddRef.current = onCornerAdd; },    [onCornerAdd]);

  // Esc cancels add-corner mode
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') setAddMode(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ── Load Google Maps API & initialise map ─────────────────────────────────
  useEffect(() => {
    if (!GMAPS_KEY) return;   // show the "key required" message instead
    if (mapInstanceRef.current) return;

    importLibrary('maps').then(() => {
      // importLibrary() populates window.google.maps as a side effect (same
      // global namespace the rest of this file already reads from directly).
      const google = window.google;
      const map = new google.maps.Map(mapRef.current, {
        center:    { lat: 39.237, lng: -77.97 },
        zoom:      15,
        mapTypeId: 'satellite',
        tilt:      0,          // keep top-down / flat view
        streetViewControl: false,
        fullscreenControl: true,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: ['satellite', 'hybrid', 'roadmap'],
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          position: google.maps.ControlPosition.TOP_RIGHT,
        },
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER,
        },
      });

      // Click → place corner (add mode), otherwise announce the nearest trace
      // point so pages with point pickers (Search) can anchor on a map click.
      map.addListener('click', (e) => {
        if (addModeRef.current) {
          onCornerAddRef.current?.(e.latLng.lat(), e.latLng.lng());
          setAddMode(false);
          return;
        }
        const nearest = findNearest(
          e.latLng.lat(), e.latLng.lng(), searchPointsRef.current
        );
        if (nearest) {
          window.dispatchEvent(new CustomEvent('trackapp:map-click', {
            detail: { distFt: nearest.distFt, lat: nearest.lat, lon: nearest.lon },
          }));
        }
      });

      // Mousemove → chart crosshair (rAF throttled)
      map.addListener('mousemove', (e) => {
        if (hoverPendingRef.current) return;
        hoverPendingRef.current = true;
        requestAnimationFrame(() => {
          hoverPendingRef.current = false;
          const nearest = findNearest(
            e.latLng.lat(), e.latLng.lng(), searchPointsRef.current
          );
          window.dispatchEvent(new CustomEvent('trackapp:map-hover', {
            detail: nearest ? { distFt: nearest.distFt } : null,
          }));
        });
      });

      map.getDiv().addEventListener('mouseleave', () => {
        window.dispatchEvent(new CustomEvent('trackapp:map-hover', { detail: null }));
      });

      mapInstanceRef.current = map;
      setMapsLoaded(true);
    }).catch(err => {
      console.error('[TrackMap] Google Maps load error:', err);
      setLoadError(err.message || String(err));
    });
  }, []);

  // Crosshair cursor in add-corner mode
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.setOptions({ draggableCursor: addMode ? 'crosshair' : null });
  }, [addMode]);

  // ── Search index (map → chart hover) ─────────────────────────────────────
  useEffect(() => {
    const pts = [];
    selectedLaps.forEach((lapIndex) => {
      const trace = lapTraces[lapIndex];
      if (!trace) return;
      trace.points.forEach((p, i) => {
        if (i % 4 !== 0 || !p.lat || !p.lon || p.dist_m == null) return;
        pts.push({ lat: p.lat, lon: p.lon, distFt: p.dist_m * M_TO_FT });
      });
    });
    searchPointsRef.current = pts;
  }, [lapTraces, selectedLaps]);

  // Reset fit-once guard whenever the trace store is cleared (new session loaded)
  useEffect(() => {
    if (!lapTraces || Object.keys(lapTraces).length === 0) {
      setDidFit(false);
    }
  }, [lapTraces]);

  // The delta ribbon is a two-driver feature; it references the first comparison
  // driver (slot 0). Additional overlaid drivers are drawn but don't feed the
  // ribbon's base-lap pick.
  const cmp0 = compareDrivers[0] || null;
  const laps2        = cmp0?.laps || null;
  const lapTraces2   = cmp0?.lapTraces || null;
  const selectedLaps2 = cmp0?.selectedLaps || null;

  // Delta ribbon needs a reference plus at least one other selected lap.
  // Same base as the Time Delta panel: fastest selected (TB included), or the
  // user's explicit override.
  const loadedLapCount =
    selectedLaps.filter(i => lapTraces[i]).length +
    (selectedLaps2 || []).filter(i => lapTraces2?.[i]).length;
  const deltaAvailable = loadedLapCount >= 2;
  const base = pickBaseLap(lapTraces, selectedLaps, lapTraces2, selectedLaps2, deltaBaseOverride);
  const baseTrace = base?.trace ?? null;
  // Label the reference lap the way the lap list does
  const baseLegendLabel = (() => {
    if (!base) return '';
    if (base.lapIndex === TB_LAP_INDEX) return 'TB';
    const lapList = base.session === 2 ? laps2 : laps;
    const num = lapDisplayNumber(lapList, base.lapIndex, baseTrace?.lap_number);
    return `${base.session === 2 ? 'D2 ' : ''}L${num}`;
  })();

  // `compareDrivers` is rebuilt as a fresh array on every App render, so it can't
  // be used directly as an effect dependency — it would tear down and recreate
  // every trace polyline on unrelated re-renders (visible as the delta ribbon
  // flickering during map pan/zoom). Derive a stable signature from only the
  // fields the draw effect actually reads.
  const compareDrawSig = useMemo(() => compareDrivers.map(d => {
    const loaded = (d.selectedLaps || []).filter(i => d.lapTraces?.[i]);
    return [
      d.slot,
      (d.selectedLaps || []).join(','),
      loaded.join(','),
      JSON.stringify(d.customColors || null),
      (d.palette || []).join(','),
    ].join('|');
  }).join(';'), [compareDrivers]);

  // ── Draw GPS traces ───────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapsLoaded) return;

    // Remove old polylines
    polylinesRef.current.forEach(pl => pl.setMap(null));
    polylinesRef.current = [];

    const allPoints = [];
    const SEG = 6;   // points per polyline segment (controls color-gradient resolution)

    // Delta mode — reference is the first selected real lap of driver 1
    let baseLookup = null;
    if (colorMode === 'delta' && deltaAvailable && baseTrace) {
      baseLookup = normalizeLookup(buildLookup(baseTrace.points));
    }

    function drawSession(traces, laps, colorPalette, customColors) {
      laps.forEach((lapIndex, colorIdx) => {
        if (lapIndex === TB_LAP_INDEX && !showTb) return;
        const trace = traces[lapIndex];
        if (!trace) return;

        const isTb = lapIndex === TB_LAP_INDEX;

        const points = trace.points.filter(p => p.lat && p.lon);
        for (const p of points) allPoints.push(p);
        if (points.length < 2) return;

        const speeds   = points.map(p => p.speed).filter(v => v != null);
        const minSpeed = speeds.length ? arrayMin(speeds) : 0;
        const maxSpeed = speeds.length ? arrayMax(speeds) : 100;
        const lapTotalDistM = points.reduce((m, p) => (p.dist_m != null && p.dist_m > m ? p.dist_m : m), 0);
        const lapColor = isTb
          ? TB_COLOR
          : ((customColors && customColors[lapIndex]) || colorPalette[colorIdx % colorPalette.length]);

        if (isTb) {
          // TB: single dashed gold polyline, ignore color-by-speed/throttle
          const pl = new window.google.maps.Polyline({
            path:          points.map(p => ({ lat: p.lat, lng: p.lon })),
            strokeColor:   TB_COLOR,
            strokeWeight:  3,
            strokeOpacity: 0,           // base line transparent — dashes drawn via icons
            icons: [{
              icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3, strokeColor: TB_COLOR },
              offset: '0', repeat: '12px',
            }],
            map,
          });
          polylinesRef.current.push(pl);
          return;
        }

        // Cumulative time delta vs the reference lap, per point (null = no data)
        let deltas = null;
        if (baseLookup?.length) {
          const t0 = points.find(p => p.time != null)?.time;
          if (t0 != null) {
            deltas = points.map(p => {
              if (p.time == null || p.dist_m == null) return null;
              const baseRel = interpolateRelTime(baseLookup, p.dist_m);
              return baseRel == null ? null : (p.time - t0) - baseRel;
            });
          }
        }

        for (let i = 0; i < points.length - 1; i += SEG) {
          const seg = points.slice(i, Math.min(i + SEG + 1, points.length));
          if (seg.length < 2) continue;

          const mid = seg[Math.floor(seg.length / 2)];
          let color;
          if (colorMode === 'sector') {
            color = sectorColor(mid.dist_m, lapTotalDistM, sectors);
          } else if (deltas) {
            // Color by local rate of time gain/loss across this segment
            const a = i, b = Math.min(i + SEG, points.length - 1);
            const dDist  = (points[b].dist_m ?? 0) - (points[a].dist_m ?? 0);
            const dDelta = (deltas[a] != null && deltas[b] != null) ? deltas[b] - deltas[a] : null;
            color = deltaSlopeColor(dDelta != null && dDist > 0 ? dDelta / dDist : null);
          } else {
            color = getPointColor(mid, colorMode, minSpeed, maxSpeed, lapColor);
          }

          const pl = new window.google.maps.Polyline({
            path:          seg.map(p => ({ lat: p.lat, lng: p.lon })),
            strokeColor:   color,
            strokeWeight:  3,
            strokeOpacity: 0.9,
            map,
          });
          polylinesRef.current.push(pl);
        }
      });
    }

    drawSession(lapTraces,  selectedLaps,  LAP_COLORS, customColors1);
    compareDrivers.forEach(d => {
      if (d.selectedLaps?.length) drawSession(d.lapTraces, d.selectedLaps, d.palette, d.customColors);
    });

    // Fit bounds once when the first traces arrive
    if (!didFit && allPoints.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      allPoints.forEach(p => bounds.extend({ lat: p.lat, lng: p.lon }));
      map.fitBounds(bounds, 40);   // 40 px padding
      setDidFit(true);
    }
  }, [lapTraces, selectedLaps, compareDrawSig, colorMode, mapsLoaded, customColors1, showTb, deltaBaseOverride, sectors]);

  // ── Chart → map crosshair ─────────────────────────────────────────────────
  useEffect(() => {
    const makeDot = (map, lat, lon, stroke) => {
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14">
        <circle cx="7" cy="7" r="5" fill="#ffffff" fill-opacity="0.9" stroke="${stroke}" stroke-width="2.5"/>
      </svg>`;
      return new window.google.maps.Marker({
        position: { lat, lng: lon },
        icon: {
          url:    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          size:   new window.google.maps.Size(14, 14),
          anchor: new window.google.maps.Point(7, 7),
        },
        zIndex: 20,
        map,
      });
    };

    const handler = (e) => {
      const map = mapInstanceRef.current;
      if (!map || !mapsLoaded) return;

      const info = e.detail;

      // Driver 1 dot
      if (!info?.lat || !info?.lon) {
        if (crosshairRef.current) crosshairRef.current.setMap(null);
      } else if (!crosshairRef.current) {
        crosshairRef.current = makeDot(map, info.lat, info.lon, '#f5c842');
      } else {
        crosshairRef.current.setMap(map);
        crosshairRef.current.setPosition({ lat: info.lat, lng: info.lon });
      }

      // Driver 2 dot
      if (!info?.lat2 || !info?.lon2) {
        if (crosshairRef2.current) crosshairRef2.current.setMap(null);
      } else if (!crosshairRef2.current) {
        crosshairRef2.current = makeDot(map, info.lat2, info.lon2, '#00cfff');
      } else {
        crosshairRef2.current.setMap(map);
        crosshairRef2.current.setPosition({ lat: info.lat2, lng: info.lon2 });
      }
    };

    window.addEventListener('trackapp:chart-hover', handler);
    // Seed from the last cursor so a late mount (mobile tab switch) shows
    // the position dot immediately.
    const last = lastCursor();
    if (last) handler({ detail: last });
    return () => window.removeEventListener('trackapp:chart-hover', handler);
  }, [mapsLoaded]);

  // ── Corner Metrics hover → halo on the corner apex ───────────────────────
  useEffect(() => {
    const handler = (e) => {
      const map = mapInstanceRef.current;
      if (!map || !mapsLoaded) return;
      const d = e.detail;
      if (!d?.lat || !d?.lon) {
        cornerHaloRef.current?.setMap(null);
        return;
      }
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="44">
        <circle cx="22" cy="22" r="18" fill="none" stroke="#f5c842" stroke-width="3" stroke-opacity="0.9"/>
        <circle cx="22" cy="22" r="20" fill="#f5c842" fill-opacity="0.15"/>
      </svg>`;
      if (!cornerHaloRef.current) {
        cornerHaloRef.current = new window.google.maps.Marker({
          position: { lat: d.lat, lng: d.lon },
          icon: {
            url:    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            size:   new window.google.maps.Size(44, 44),
            anchor: new window.google.maps.Point(22, 22),
          },
          clickable: false,
          zIndex: 30,
          map,
        });
      } else {
        cornerHaloRef.current.setPosition({ lat: d.lat, lng: d.lon });
        cornerHaloRef.current.setMap(map);
      }
    };
    window.addEventListener('trackapp:corner-highlight', handler);
    return () => {
      window.removeEventListener('trackapp:corner-highlight', handler);
      cornerHaloRef.current?.setMap(null);
    };
  }, [mapsLoaded]);

  // ── Chart zoom → map fitBounds ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const map = mapInstanceRef.current;
      if (!map || !mapsLoaded) return;

      const detail = e.detail;
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;

      const addTraceToBounds = (traces, laps, distMin, distMax) => {
        laps.forEach(lapIndex => {
          const trace = traces[lapIndex];
          if (!trace) return;
          trace.points.forEach(p => {
            if (!p.lat || !p.lon) return;
            if (distMin != null) {
              const distFt = (p.dist_m ?? 0) * M_TO_FT;
              if (distFt < distMin || distFt > distMax) return;
            }
            bounds.extend({ lat: p.lat, lng: p.lon });
            hasPoints = true;
          });
        });
      };

      if (!detail) {
        // Reset — fit all selected lap points
        addTraceToBounds(lapTraces, selectedLaps, null, null);
        compareDrivers.forEach(d => {
          if (d.selectedLaps?.length) addTraceToBounds(d.lapTraces, d.selectedLaps, null, null);
        });
      } else {
        const { distFtMin, distFtMax } = detail;
        addTraceToBounds(lapTraces, selectedLaps, distFtMin, distFtMax);
        compareDrivers.forEach(d => {
          if (d.selectedLaps?.length) addTraceToBounds(d.lapTraces, d.selectedLaps, distFtMin, distFtMax);
        });
      }

      if (hasPoints) map.fitBounds(bounds, 40);
    };

    window.addEventListener('trackapp:chart-zoom', handler);
    return () => window.removeEventListener('trackapp:chart-zoom', handler);
  }, [lapTraces, selectedLaps, compareDrivers, mapsLoaded]);

  // ── Canonical start/finish marker ─────────────────────────────────────────
  // Shows where the track definition's timing line sits — makes it visible
  // when a session's laps were segmented from a different (device-beacon) S/F.
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapsLoaded) return;
    sfMarkerRef.current?.setMap(null);
    sfMarkerRef.current = null;
    if (!Number.isFinite(startFinish?.lat) || !Number.isFinite(startFinish?.lon)) return;
    sfMarkerRef.current = new window.google.maps.Marker({
      position:  { lat: startFinish.lat, lng: startFinish.lon },
      map,
      clickable: false,
      title:     'Start / Finish (track)',
      icon: {
        url:    sfIconUrl(),
        size:   new window.google.maps.Size(22, 26),
        anchor: new window.google.maps.Point(3, 25),
      },
      zIndex: 5,
    });
    return () => {
      sfMarkerRef.current?.setMap(null);
      sfMarkerRef.current = null;
    };
  }, [startFinish, mapsLoaded]);

  // ── Corner markers ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapsLoaded) return;

    // Clear old markers
    Object.values(cornerMarkersRef.current).forEach(({ marker, infoWindow }) => {
      marker.setMap(null);
      infoWindow?.close();
    });
    cornerMarkersRef.current = {};
    if (!corners?.length) return;

    corners.forEach(corner => {
      const marker = new window.google.maps.Marker({
        position: { lat: corner.lat, lng: corner.lon },
        map: showTurnMarkers ? map : null,
        draggable: canEditCorners,
        title: corner.label || corner.id,
        icon: {
          url:    cornerIconUrl(corner.label || corner.id),
          size:   new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
      });

      // Popup content (dark-themed)
      const wrap = document.createElement('div');
      wrap.style.cssText = `
        font-family:system-ui,sans-serif; padding:6px 4px 4px;
        min-width:168px; color:#e0e0e0; background:#1e1e1e;
      `;

      if (canEditCorners) {
        const labelRow = document.createElement('div');
        labelRow.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:8px;';

        const input = document.createElement('input');
        input.value = corner.label || corner.id;
        input.style.cssText = `
          background:#111;color:#fff;border:1px solid #444;
          padding:4px 6px;border-radius:4px;flex:1;min-width:0;font-size:12px;
        `;

        const saveBtn = document.createElement('button');
        saveBtn.textContent = 'Save';
        saveBtn.style.cssText = `
          background:#e8411a;color:#fff;border:none;
          padding:4px 8px;border-radius:4px;cursor:pointer;font-size:12px;
        `;
        saveBtn.onclick = () => {
          onCornerLabelChange?.(corner.id, input.value);
          infoWindow.close();
        };
        labelRow.append(input, saveBtn);

        const removeBtn = document.createElement('button');
        removeBtn.textContent = '✕ Remove this turn';
        removeBtn.style.cssText = `
          width:100%;background:transparent;color:#888;border:1px solid #333;
          padding:4px 8px;border-radius:4px;cursor:pointer;font-size:12px;
        `;
        removeBtn.onmouseenter = () => { removeBtn.style.color='#e8411a'; removeBtn.style.borderColor='#e8411a44'; };
        removeBtn.onmouseleave = () => { removeBtn.style.color='#888';   removeBtn.style.borderColor='#333'; };
        removeBtn.onclick = () => { onCornerRemove?.(corner.id); infoWindow.close(); };

        wrap.append(labelRow, removeBtn);
      } else {
        const nameRow = document.createElement('div');
        nameRow.textContent = corner.label || corner.id;
        nameRow.style.cssText = 'font-size:12px;font-weight:600;';
        wrap.append(nameRow);
      }

      const infoWindow = new window.google.maps.InfoWindow({ content: wrap, disableAutoPan: false });

      marker.addListener('click', () => infoWindow.open({ map, anchor: marker }));
      if (canEditCorners) {
        marker.addListener('dragend', (e) => {
          onCornerMove?.(corner.id, e.latLng.lat(), e.latLng.lng());
        });
      }

      cornerMarkersRef.current[corner.id] = { marker, infoWindow };
    });
  }, [corners, onCornerMove, onCornerLabelChange, onCornerRemove, mapsLoaded, showTurnMarkers, canEditCorners]);

  // ── Turn marker visibility toggle ─────────────────────────────────────────
  useEffect(() => {
    const map = showTurnMarkers ? mapInstanceRef.current : null;
    Object.values(cornerMarkersRef.current).forEach(({ marker, infoWindow }) => {
      marker.setMap(map);
      if (!showTurnMarkers) infoWindow?.close();
    });
  }, [showTurnMarkers]);

  // ── Per-lap apex markers ──────────────────────────────────────────────────
  useEffect(() => {
    const map = mapInstanceRef.current;
    apexMarkersRef.current.forEach(m => m.setMap(null));
    apexMarkersRef.current = [];

    if (!map || !mapsLoaded || !showPerLapApex || !metrics?.by_corner) return;

    // Reference apex — white diamond SVG, pixel-sized, one per corner
    corners?.forEach(corner => {
      if (!corner.apex_lat || !corner.apex_lon) return;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12">
        <polygon points="6,1 11,6 6,11 1,6"
          fill="rgba(255,255,255,0.2)" stroke="white" stroke-width="1.5"/>
      </svg>`;
      const refMarker = new window.google.maps.Marker({
        position: { lat: corner.apex_lat, lng: corner.apex_lon },
        map,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          size:   new window.google.maps.Size(12, 12),
          anchor: new window.google.maps.Point(6, 6),
        },
        title: `${corner.label || corner.id} reference apex`,
        zIndex: 5,
      });
      apexMarkersRef.current.push(refMarker);
    });

    // Per-lap apex — filled colored circle SVG, pixel-sized
    Object.values(metrics.by_corner).forEach(cdata => {
      (cdata.per_lap || []).forEach(entry => {
        const { lap_index, driver_apex_lat, driver_apex_lon } = entry;
        if (!driver_apex_lat || !driver_apex_lon) return;
        const color = getLapColor(lap_index, selectedLaps, LAP_COLORS, customColors1) || '#aaaaaa';
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10">
          <circle cx="5" cy="5" r="4" fill="${color}" stroke="white" stroke-width="1"/>
        </svg>`;
        const dot = new window.google.maps.Marker({
          position: { lat: driver_apex_lat, lng: driver_apex_lon },
          map,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            size:   new window.google.maps.Size(10, 10),
            anchor: new window.google.maps.Point(5, 5),
          },
          title: `Lap ${lap_index} apex`,
          zIndex: 10,
        });
        apexMarkersRef.current.push(dot);
      });
    });

  }, [showPerLapApex, metrics, corners, selectedLaps, mapsLoaded, customColors1]);

  // ── Render ────────────────────────────────────────────────────────────────
  const handleMouseEnter = () => {
    clearTimeout(hideTimerRef.current);
    setControlsVisible(true);
  };
  const handleMouseLeave = () => {
    hideTimerRef.current = setTimeout(() => setControlsVisible(false), 500);
  };

  return (
    <div className={styles.wrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>

      {/* No API key — show setup instructions */}
      {!GMAPS_KEY && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: '#0d0d0d', zIndex: 20,
        }}>
          <div style={{
            background: '#1a1a1a', border: '1px solid #333',
            borderRadius: 10, padding: '2rem', maxWidth: 380, textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', marginBottom: '0.75rem' }}>
              Google Maps API Key Required
            </div>
            <div style={{ fontSize: '0.83rem', color: '#888', lineHeight: 1.7 }}>
              Add your key to{' '}
              <code style={{ background: '#111', padding: '1px 5px', borderRadius: 3, color: '#ccc' }}>
                frontend/.env.local
              </code>
              :<br />
              <code style={{ background: '#111', padding: '4px 8px', borderRadius: 3, color: '#e8411a', display: 'inline-block', marginTop: 8 }}>
                VITE_GOOGLE_MAPS_API_KEY=your_key_here
              </code>
              <br /><br />
              Get a key at{' '}
              <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer"
                 style={{ color: '#4a9eff' }}>
                console.cloud.google.com
              </a>
              {' '}— enable the <strong style={{ color: '#ccc' }}>Maps JavaScript API</strong>.
            </div>
          </div>
        </div>
      )}

      {/* API load error */}
      {loadError && (
        <div style={{
          position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
          background: '#2a0a0a', border: '1px solid #e8411a',
          padding: '6px 14px', borderRadius: 6,
          color: '#e8411a', fontSize: '0.83rem', zIndex: 20,
        }}>
          ⚠ Maps failed to load: {loadError}
        </div>
      )}

      <div ref={mapRef} className={styles.map} />

      {/* Controls overlay — visible only when mouse is in the panel */}
      <div className={`${styles.controls} ${(controlsVisible || isMobile) ? styles.controlsVisible : ''}`}>
        <span className={styles.controlLabel}>Color by:</span>
        {[
          'lap', 'speed', 'delta', 'throttle', 'brake',
          ...(sectors?.length ? ['sector'] : []),
        ].map(mode => (
          <button key={mode}
            className={`${styles.btn} ${colorMode === mode ? styles.active : ''}`}
            disabled={mode === 'delta' && !deltaAvailable}
            style={mode === 'delta' && !deltaAvailable ? { opacity: 0.4 } : undefined}
            title={mode === 'delta'
              ? (deltaAvailable ? 'Color by time gained/lost vs the base lap (fastest selected, or the Time Delta panel\'s Base pick)'
                                : 'Select two or more laps to color by time delta')
              : mode === 'sector' ? 'Color by user-defined sector'
              : undefined}
            onClick={() => setColorMode(mode)}>
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
        {canEditCorners && (
          <>
            <div className={styles.divider} />
            <button
              className={`${styles.btn} ${addMode ? styles.addActive : ''}`}
              onClick={() => setAddMode(m => !m)}
              title="Click on the map to place a new turn marker">
              {addMode
                ? <><MapPin size={12} /> Click map to place…</>
                : <><Plus size={12} /> Add Turn</>}
            </button>
          </>
        )}
        {canEditCorners && turnCount !== null && (
          <>
            <div className={styles.divider} />
            <div className={styles.detectWrap}>
              <button
                className={`${styles.btn} ${detectOpen ? styles.active : ''}`}
                onClick={() => setDetectOpen(o => !o)}
                title="Turn-detection settings">
                <Settings size={12} /> Turns: {turnCount}
              </button>
              {detectOpen && (
                <div className={styles.detectPopover}>
                  <div className={styles.detectRow}>
                    <label className={styles.controlLabel}>Turn count</label>
                    <input
                      type="number" min={1} max={99} value={turnCount}
                      onChange={e => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v > 0) onTurnCountChange?.(v);
                      }}
                      className={styles.turnInput}
                      title="Force an exact number of turns"
                    />
                  </div>
                  <div className={styles.detectCol}>
                    <label className={styles.controlLabel}>Sensitivity</label>
                    <div className={styles.segGroup}>
                      {['low', 'medium', 'high'].map(s => (
                        <button
                          key={s}
                          className={`${styles.segBtn} ${sensitivity === s ? styles.segActive : ''}`}
                          onClick={() => onSensitivityChange?.(s)}
                          title={s === 'low' ? 'Only major corners'
                               : s === 'high' ? 'Include gentle kinks'
                               : 'Balanced (default)'}>
                          {s === 'low' ? 'Low' : s === 'medium' ? 'Med' : 'High'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className={styles.detectRow}>
                    <button
                      className={styles.btn}
                      onClick={() => onRenumberTurns?.()}
                      disabled={!corners?.length}
                      title="Reorder turns by position along the lap and relabel Turn 1..N (use after adding or moving turns)">
                      Renumber by position
                    </button>
                  </div>
                  <div className={styles.detectHint}>
                    Sensitivity re-detects from lap geometry and resets the turn
                    count. Set an exact count above to override.
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <div className={styles.divider} />
        <button
          className={`${styles.btn} ${showTurnMarkers ? styles.active : ''}`}
          onClick={onToggleShowTurnMarkers}
          disabled={!corners?.length}
          title={corners?.length ? 'Toggle turn markers' : 'No turn markers defined'}
          style={{ opacity: corners?.length ? 1 : 0.4 }}>
          Turns
        </button>
        <div className={styles.divider} />
        <button
          className={`${styles.btn} ${showPerLapApex ? styles.active : ''}`}
          onClick={onTogglePerLapApex}
          disabled={!metrics}
          title={metrics ? 'Toggle per-lap apex markers' : 'Calculate metrics first'}
          style={{ opacity: metrics ? 1 : 0.4 }}>
          Apexes
        </button>
        <div className={styles.divider} />
        <HelpButton panel="trackmap" page="sessions" className={styles.btn} />
      </div>

      {/* Bottom-left overlay stack: ribbon color legend above the corner chips */}
      <div className={styles.bottomOverlays}>
      {colorMode !== 'lap' && (
        <div className={styles.colorLegend}>
          {colorMode === 'speed' && (
            <>
              <span>Slow</span>
              <span className={styles.legendBar}
                style={{ background: 'linear-gradient(to right, rgb(0,200,30), rgb(255,200,30), rgb(255,0,0))' }} />
              <span>Fast</span>
            </>
          )}
          {colorMode === 'throttle' && (
            <>
              <span>0%</span>
              <span className={styles.legendBar}
                style={{ background: 'linear-gradient(to right, rgb(200,0,20), rgb(0,220,20))' }} />
              <span>100% throttle</span>
            </>
          )}
          {colorMode === 'brake' && (
            <>
              <span>Brake</span>
              <span className={styles.legendBar}
                style={{ background: 'linear-gradient(to right, rgb(255,0,0), #ffffff, rgb(0,255,0))' }} />
              <span>Accel</span>
            </>
          )}
          {colorMode === 'delta' && (deltaAvailable && baseTrace ? (
            <>
              <span>Gaining</span>
              <span className={styles.legendBar}
                style={{ background: 'linear-gradient(to right, rgb(0,255,0), #ffffff, rgb(255,0,0))' }} />
              <span>Losing</span>
              <span className={styles.legendNote}>
                vs {baseLegendLabel} ({baseTrace.lap_time_str})
              </span>
            </>
          ) : (
            <span className={styles.legendNote}>Select two or more laps to color by time delta</span>
          ))}
        </div>
      )}

      {/* Sector legend */}
      {colorMode === 'sector' && sectors?.length > 0 && (
        <div className={styles.cornerLegend}>
          {sectors.map((s, i) => (
            <span key={s.id} className={styles.cornerChip}>
              <span className={styles.dirDot} style={{ background: LAP_COLORS[i % LAP_COLORS.length] }} />
              {s.label || s.id}
            </span>
          ))}
        </div>
      )}

      {/* Corner legend */}
      {corners?.length > 0 && showTurnMarkers && (
        <div className={styles.cornerLegend}>
          {corners.map(c => (
            <span key={c.id} className={styles.cornerChip}>
              <span className={`${styles.dirDot} ${c.direction === 'left' ? styles.left : styles.right}`} />
              {c.label || c.id}
            </span>
          ))}
        </div>
      )}
      </div>{/* /bottomOverlays */}

      {addMode && (
        <div className={styles.addHint}>
          Click anywhere on the track to place a new turn. Press Esc to cancel.
        </div>
      )}
    </div>
  );
}
